import * as React from "react";

import {
  focusOnNextElement,
  IBasePickerStyleProps,
  IBasePickerStyles,
  IStyleFunction,
  mergeFuncStyles,
  mergeStyleSets,
  setComponentRef
} from "@bps/fluent-ui";

import {
  ConvertToSingleItemFetchProps,
  ConvertToSinglePickerProps,
  ExtendedPickerProps
} from "./ExtendedPicker.types.ts";
import { IExtendedBasePicker } from "./IExtendedBasePicker.ts";

type InnerState = {
  isInputEmpty: boolean;
};

/**
 * Higher order component that takes an extended picker component and
 * that will turn it into a single selection picker
 * - selected item will display as ghost text when the picker has focus until the user starts typing
 * - selected item will have a transparent background to make it look like a select
 * - the remove button is moved to the right side of the picker
 * - clicking anywhere on the picker gives focus
 * - when a selection is made, the picker will give focus to the next tabbable element (configurable)
 * - expose selectedItem  and onChange as a single string to avoid working with an array of values when only one value can be selected at most
 * @param Component
 * @param singlePickerStyles a styling function that is specific to the type of picker (Persona, Tag, ...)
 */
export const extendToSinglePicker = <
  P extends Pick<
    ExtendedPickerProps<TSuggestionItem, TItem>,
    "onChange" | "componentRef" | "styles" | "onInputChange"
  >,
  TSuggestionItem,
  TItem extends any
>(
  Component: React.FunctionComponent<P>,
  singlePickerStyles: IStyleFunction<
    IBasePickerStyleProps & { isInputEmpty: boolean },
    IBasePickerStyles
  >
): React.ComponentType<ConvertToSinglePickerProps<P, TItem>> => {
  type InnerProps = ConvertToSinglePickerProps<P, TItem>;

  class SinglePickerComponent extends React.Component<InnerProps, InnerState> {
    static readonly displayName = `${
      Component.displayName || (Component as any).name
    }`;

    constructor(props: InnerProps) {
      super(props);
      this.state = {
        isInputEmpty: true
      };
    }

    private basePicker: IExtendedBasePicker<TSuggestionItem> | null = null;

    render() {
      const { onChange, focusNextOnChange, selectedKey, styles, ...props } =
        this.props as InnerProps;

      const onPickerChange: P["onChange"] = onChange
        ? (keys, items) => {
            onChange(
              keys && keys.length ? keys[keys.length - 1] : undefined,
              items && items.length ? items[items.length - 1] : undefined
            );
            if (keys?.length && focusNextOnChange) {
              focusOnNextElement(
                this.basePicker?.input?.current?.inputElement ?? null
              );
            }
          }
        : undefined;

      const newStyles: IStyleFunction<
        IBasePickerStyleProps,
        IBasePickerStyles
      > = mergeFuncStyles(
        (props: IBasePickerStyleProps): Partial<IBasePickerStyles> =>
          mergeStyleSets({
            ...singlePickerStyles({
              ...props,
              isInputEmpty: this.state.isInputEmpty
            }),
            input: {
              position: "absolute",
              // make the input take focus when clicking anywhere on the picker but leave out enough space for the
              // remove button and search icon
              width:
                selectedKey || !props.isFocused
                  ? "calc(100% - 43px)"
                  : "calc(100% - 11px)"
            },
            subComponentStyles: {
              callout: {
                root: {
                  button: {
                    padding: "6px 12px 7px"
                  }
                }
              }
            },
            text: {
              boxSizing: "content-box",
              backgroundColor: props.theme!.palette.white
            }
          }) as any, // TODO - improve this
        styles
      );

      return (
        <Component
          {...(props as P)}
          onChange={onPickerChange}
          componentRef={ref => {
            this.basePicker = ref;
            setComponentRef(this.props.componentRef, ref);
          }}
          selectedKeys={selectedKey ? [selectedKey] : []}
          itemLimit={2}
          styles={newStyles}
          onInputChange={val => {
            // we don't want to modify the input string here
            // we want to change our internal state whenever the input
            // changes between empty and non-empty
            const isInputEmpty = !val;
            if (this.state.isInputEmpty !== isInputEmpty) {
              this.setState({ isInputEmpty });
            }
            return this.props.onInputChange
              ? this.props.onInputChange(val)
              : val;
          }}
        />
      );
    }
  }

  return SinglePickerComponent;
};

/**
 * HOC that wraps onFetchItems to work with a single item onFetchItem
 * @param Component
 */
export const withSingleItemFetch = <
  P extends Pick<ExtendedPickerProps<TSuggestionItem, TItem>, "onFetchItems">,
  TSuggestionItem,
  TItem extends any
>(
  Component: React.FunctionComponent<P>
): React.ComponentType<ConvertToSingleItemFetchProps<P, TItem>> => {
  type InnerProps = ConvertToSingleItemFetchProps<P, TItem>;

  class SingleFetchComponent extends React.Component<InnerProps> {
    static readonly displayName = `${
      Component.displayName || (Component as any).name
    }`;

    render() {
      const { onFetchItem, ...props } = this.props;
      const onFetchItems: P["onFetchItems"] = async items => {
        if (!items.length) {
          return [];
        }
        return [await Promise.resolve(onFetchItem(items[0]))];
      };

      return (
        <Component {...(props as unknown as P)} onFetchItems={onFetchItems} />
      );
    }
  }

  return SingleFetchComponent;
};
